import { CompanyBenefitsConfig, CompanyTaxConfig } from '../types/company';

export interface CLTCostBreakdown {
    salarioBase: number;
    decimoTerceiro: number;
    feriasProvisao: number;
    fgts: number;
    encargos: number;
    valeTransporte: number;
    valeRefeicao: number;
    valeAlimentacao: number;
    auxilioSaude: number;
    custoTotal: number;
    fatorMultiplicador: number;
}

export interface SCPCostBreakdown {
    salarioBase: number;
    decimoTerceiro: number;
    feriasProvisao: number;
    custoTotal: number;
    fatorMultiplicador: number;
}

export interface PJCostBreakdown {
    salarioBase: number;
    valeTransporte: number;
    valeRefeicao: number;
    valeAlimentacao: number;
    auxilioSaude: number;
    custoTotal: number;
    fatorMultiplicador: number;
}

export class CLTCalculator {
    private benefits: CompanyBenefitsConfig;
    private taxConfig: CompanyTaxConfig;

    constructor(benefits: CompanyBenefitsConfig, taxConfig: CompanyTaxConfig) {
        this.benefits = benefits;
        this.taxConfig = taxConfig;
    }

    private calculateINSS(baseValue: number): number {
        let inss = 0;
        let remainingBase = baseValue;

        for (const range of this.taxConfig.inssRanges) {
            const rangeValue = Math.min(remainingBase, range.tetoFaixa);
            inss += (rangeValue * range.aliquota) / 100;
            remainingBase -= rangeValue;
            if (remainingBase <= 0) break;
        }

        return inss;
    }

    private calculateIRPF(baseValue: number): number {
        // Encontra a faixa correta do IRPF
        const range = this.taxConfig.irpfRanges.find(r => baseValue <= r.tetoFaixa) 
            || this.taxConfig.irpfRanges[this.taxConfig.irpfRanges.length - 1];

        if (!range || baseValue <= this.taxConfig.irpfRanges[0].tetoFaixa) {
            return 0; // Isento
        }

        return (baseValue * range.aliquota / 100) - range.deducao;
    }

    private calculateFeriasProvision(salarioBase: number): number {
        // Calcula férias totais (salário + 1/3)
        const baseFeriasTotal = salarioBase * (1 + 1/3);
        
        // Calcula INSS sobre férias totais
        const inssFerias = this.calculateINSS(baseFeriasTotal);
        
        // Base para IRPF das férias
        const baseIrpfFerias = baseFeriasTotal - inssFerias;
        
        // Calcula IRPF sobre férias totais
        const irpfFerias = this.calculateIRPF(baseIrpfFerias);
        
        // Calcula 1/3 do salário bruto e subtrai IRPF proporcional
        const umTercoFerias = salarioBase * (1/3);
        const umTercoLiquido = umTercoFerias - irpfFerias;
        
        // Provisão mensal (divide por 11 meses)
        return umTercoLiquido / 11;
    }

    private calculateEncargos(salarioBase: number): number {
        // Base mensal total para encargos
        const decimoTerceiro = salarioBase / 12;
        const ferias = salarioBase / 12;
        const umTercoFerias = (salarioBase * 1/3) / 12;
        const baseMensalTotal = salarioBase + decimoTerceiro + ferias + umTercoFerias;

        // Pega o teto do INSS (última faixa)
        const tetoINSS = this.taxConfig.inssRanges[this.taxConfig.inssRanges.length - 1].tetoFaixa;
        
        // Limita a base ao teto do INSS
        const baseLimitada = Math.min(baseMensalTotal, tetoINSS);

        // Soma todas as alíquotas de encargos
        const aliquotaTotal = 
            this.taxConfig.inssPatronal +
            this.taxConfig.ratSat +
            this.taxConfig.inssTerceiros +
            this.taxConfig.salarioEducacao;

        return baseLimitada * (aliquotaTotal / 100);
    }

    public calculateCosts(salarioBase: number): CLTCostBreakdown {
        // Cálculo do 13º salário (provisão mensal)
        const decimoTerceiro = salarioBase / 12;

        // Cálculo das férias (provisão mensal)
        const feriasProvisao = (salarioBase * 4/3) / 12; // Inclui 1/3 de férias

        // Cálculo do FGTS
        const fgts = (salarioBase + decimoTerceiro + feriasProvisao) * (this.taxConfig.fgts / 100);

        // Cálculo dos encargos sociais usando o método corrigido
        const encargos = this.calculateEncargos(salarioBase);

        // Cálculo dos benefícios
        const valeTransporte = Math.max(0, 
            (this.benefits.valeTransporteDiario * this.benefits.diasUteisMedia) - 
            (salarioBase * (this.benefits.descontoVTPercentual / 100))
        );
        const valeRefeicao = this.benefits.valeRefeicaoDiario * this.benefits.diasUteisMedia;
        const valeAlimentacao = this.benefits.valeAlimentacaoDiario * this.benefits.diasUteisMedia;
        const auxilioSaude = this.benefits.auxilioSaudeMensal;

        // Cálculo do custo total
        const custoTotal = salarioBase + 
                         decimoTerceiro + 
                         feriasProvisao + 
                         fgts + 
                         encargos + 
                         valeTransporte + 
                         valeRefeicao + 
                         valeAlimentacao + 
                         auxilioSaude;

        // Cálculo do fator multiplicador
        const fatorMultiplicador = custoTotal / salarioBase;

        return {
            salarioBase,
            decimoTerceiro,
            feriasProvisao,
            fgts,
            encargos,
            valeTransporte,
            valeRefeicao,
            valeAlimentacao,
            auxilioSaude,
            custoTotal,
            fatorMultiplicador
        };
    }

    public calculateSCPCosts(salarioBase: number): SCPCostBreakdown {
        // Cálculo do 13º salário (provisão mensal)
        const decimoTerceiro = salarioBase / 12;

        // Cálculo das férias (provisão mensal)
        const feriasProvisao = salarioBase / 12;

        // Cálculo do custo total
        const custoTotal = salarioBase + decimoTerceiro + feriasProvisao;

        // Cálculo do fator multiplicador
        const fatorMultiplicador = custoTotal / salarioBase;

        return {
            salarioBase,
            decimoTerceiro,
            feriasProvisao,
            custoTotal,
            fatorMultiplicador
        };
    }

    public calculatePJCosts(salarioBase: number): PJCostBreakdown {
        // Cálculo dos benefícios
        const valeTransporte = this.benefits.valeTransporteDiario * this.benefits.diasUteisMedia;
        const valeRefeicao = this.benefits.valeRefeicaoDiario * this.benefits.diasUteisMedia;
        const valeAlimentacao = this.benefits.valeAlimentacaoDiario * this.benefits.diasUteisMedia;
        const auxilioSaude = this.benefits.auxilioSaudeMensal;

        // Cálculo do custo total
        const custoTotal = salarioBase + 
                         valeTransporte + 
                         valeRefeicao + 
                         valeAlimentacao + 
                         auxilioSaude;

        // Cálculo do fator multiplicador
        const fatorMultiplicador = custoTotal / salarioBase;

        return {
            salarioBase,
            valeTransporte,
            valeRefeicao,
            valeAlimentacao,
            auxilioSaude,
            custoTotal,
            fatorMultiplicador
        };
    }
} 